/**
 * ProposalDraft — `/proposals/{id}/draft` view (Phase G4B CP2).
 *
 * Renders all 11 proposal sections as `SectionCard`s, mixing:
 *   - **passed**: markdown-lite content + tiered confidence chip +
 *     inline edit + regenerate
 *   - **fail_closed**: fallback message + fail_reason chip +
 *     "Draft manually" + regenerate
 *
 * Additional surface:
 *   - Header with working title + Generated status pill
 *   - Persistent AI-disclosure banner (reused from wizard)
 *   - Budget indicator in the header
 *   - "Back to wizard" (routes to /proposals/{id}/edit?step=8) +
 *     "Back to Library" (routes to /proposals)
 *
 * Free-tier / feature-flag gates (G4C CP2 polish 2026-07-20):
 *   - Free tier hitting `GET /proposals/{id}` → 402 → fetch
 *     `/proposals/{id}/preview` and render a read-only shell with
 *     an inline upgrade CTA. NO redirect to `/proposals` — the
 *     `/draft` URL stays intact.
 *   - Feature flag off is gated by `ProposalsFeatureRoute` in App.js
 *     — the API layer still returns `feature_disabled` on the
 *     `/proposals/{id}` call, which we bounce to `/proposals` where
 *     the feature-flag shell renders.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  ShieldAlert,
  BookOpen,
  Info,
  Lock,
  Target,
} from "lucide-react";
import AppNav from "@/components/app/AppNav";
import { api, formatApiError } from "@/api/client";
import ExportDropdown from "@/components/proposals/ExportDropdown";
import SectionCard from "@/components/proposals/SectionCard";
import CoachPanel from "@/components/proposals/CoachPanel";
import PremiumUpgradePanel from "@/components/grants/PremiumUpgradePanel";
import { SECTION_KEYS, statusMeta } from "@/components/proposals/utils";
import { sectionDisplayTitle } from "@/components/proposals/quality_helpers";
import { useAuth } from "@/auth/AuthContext";


function formatResetDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch (_) {
    return iso;
  }
}


function FreeTierPreview({ preview }) {
  // Free-tier read-only shell. Rendered inline on
  // /proposals/{id}/draft when the caller does not have Premium.
  // Server has already normalised the snippets (max 40 chars,
  // markup-stripped, banned-phrase swept) so we render them as
  // plain text without further sanitisation.
  const sectionKeys = preview?.section_keys || [];
  const snippets = preview?.snippets || {};
  return (
    <div data-testid="draft-free-tier-preview" className="mt-10 space-y-6">
      <header className="flex flex-col gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--ds-color-ink-subtle)]">
          Grant Writer · Preview
        </p>
        <h1
          data-testid="draft-preview-title"
          className="text-[20px] font-semibold md:text-[22px] tracking-tight text-[var(--ds-color-ink)]"
        >
          {preview?.title || "Untitled proposal"}
        </h1>
      </header>

      <PremiumUpgradePanel scope="proposals" />

      <section
        data-testid="draft-preview-sections"
        className="rounded-[var(--ds-radius-card)] border border-[var(--ds-color-border)] bg-[var(--ds-color-surface)] p-6 flex flex-col gap-3"
      >
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-[var(--ds-color-accent)]" />
          <h3 className="text-[14px] font-semibold text-[var(--ds-color-ink)]">
            Section outline
          </h3>
        </div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)] leading-relaxed">
          Upgrade to Premium to read the full drafts, edit sections
          inline, regenerate individual passages, and export as PDF,
          DOCX, or TXT.
        </p>
        <ul className="mt-2 space-y-2">
          {sectionKeys.map((key) => {
            const snippet = snippets[key];
            return (
              <li
                key={key}
                data-testid={`draft-preview-section-${key}`}
                className="rounded-[var(--ds-radius-card)] border border-[var(--ds-color-border)] bg-[var(--ds-color-surface-muted)]/40 px-4 py-3"
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-[13px] font-semibold text-[var(--ds-color-ink)]">
                    {sectionDisplayTitle(key)}
                  </span>
                  {!snippet && (
                    <span
                      data-testid={`draft-preview-section-${key}-locked`}
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em]"
                      style={{
                        background: "var(--ds-color-surface-muted)",
                        border: "1px solid var(--ds-color-surface-muted)",
                        color: "var(--ds-color-ink-muted)",
                      }}
                    >
                      <Lock className="w-3 h-3" /> Locked
                    </span>
                  )}
                </div>
                {snippet && (
                  <p
                    data-testid={`draft-preview-section-${key}-snippet`}
                    className="mt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]"
                  >
                    {snippet}
                    <span aria-hidden> …</span>
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}


export default function ProposalDraft() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [budget, setBudget] = useState(null);
  const [budgetLoading, setBudgetLoading] = useState(true);
  // G4C CP2 polish (2026-07-20) — Free-tier preview mode. When the
  // `/proposals/{id}` GET returns 402 `proposals_premium_only`, we
  // fetch `/proposals/{id}/preview` (no premium gate) and render
  // the read-only shell with an inline upgrade CTA instead of
  // redirecting the user off the `/draft` URL.
  const [preview, setPreview] = useState(null);

  const isFreeTier = user?.access_level !== "premium";

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setPreview(null);
    try {
      const res = await api.get(`/proposals/${id}`);
      setProposal(res.data);
    } catch (err) {
      // Discriminate by `code` first — see Proposals.jsx / wizard for
      // rationale. G4B CP2 rerun P0 fix.
      const code = err?.response?.data?.detail?.code;
      const status = err?.response?.status;
      if (code === "feature_disabled") {
        navigate("/proposals", { replace: true });
        return;
      }
      if (code === "proposals_premium_only") {
        // Free tier — swap to preview mode (G4C CP2 polish
        // 2026-07-20). Fetch the un-gated /preview and render the
        // FreeTierPanel inline. NO redirect.
        try {
          const previewRes = await api.get(`/proposals/${id}/preview`);
          setPreview(previewRes.data);
        } catch (previewErr) {
          const previewStatus = previewErr?.response?.status;
          if (previewStatus === 404) {
            setLoadError("Proposal not found or no longer accessible.");
          } else {
            setLoadError(formatApiError(previewErr));
          }
        }
        return;
      }
      if (status === 404) {
        setLoadError("Proposal not found or no longer accessible.");
      } else {
        setLoadError(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const loadBudget = useCallback(async () => {
    setBudgetLoading(true);
    try {
      const res = await api.get("/proposals/generation/budget");
      setBudget(res.data);
    } catch (_err) {
      setBudget(null);
    } finally {
      setBudgetLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadBudget();
  }, [load, loadBudget]);

  const handleSectionUpdated = useCallback((updatedProposal) => {
    setProposal(updatedProposal);
  }, []);

  const sections = proposal?.sections || {};
  const hasAnySection = Object.keys(sections).length > 0;
  const nextReset = formatResetDate(budget?.next_reset_at);
  const status = proposal?.status || "draft";
  const meta = statusMeta(status);

  return (
    <main className="min-h-screen text-[var(--ds-color-ink)] bg-[var(--ds-color-background)] pb-32">
      <AppNav />
      <div className="mx-auto max-w-6xl px-5 md:px-8 pt-8">
        <div className="flex items-center gap-4 flex-wrap">
          <Link
            to="/proposals"
            data-testid="draft-back-to-library"
            className="inline-flex items-center gap-1.5 text-[13px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Proposal Library
          </Link>
          {proposal?.id && (
            <Link
              to={`/proposals/${proposal.id}/edit?step=8`}
              data-testid="draft-back-to-wizard"
              className="inline-flex items-center gap-1.5 text-[13px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] transition-colors"
            >
              <BookOpen className="w-4 h-4" /> Back to wizard (Review)
            </Link>
          )}
        </div>

        {loading ? (
          <div
            data-testid="draft-loading"
            className="mt-10 inline-flex items-center gap-2 text-[var(--ds-color-ink-muted)]"
          >
            <Loader2 className="w-4 h-4 animate-spin" /> Loading proposal…
          </div>
        ) : preview ? (
          // G4C CP2 polish (2026-07-20) — Free-tier read-only shell.
          // Renders the proposal's title + section-key list with
          // short (<=40 char) plain-text snippets + inline upgrade
          // CTA. No export dropdown, no regenerate, no wizard
          // shortcut — those all require Premium. The URL stays on
          // /proposals/{id}/draft so the upgrade path is stable.
          <FreeTierPreview preview={preview} />
        ) : loadError ? (
          <div
            data-testid="draft-load-error"
            className="mt-10 rounded-[var(--ds-radius-card)] border p-6 text-[13px]"
            style={{
              background: "var(--ds-color-danger-soft)",
              borderColor: "var(--ds-color-danger)",
              color: "var(--ds-color-danger)",
            }}
          >
            {loadError}
          </div>
        ) : (
          <>
            <header className="mt-6 flex items-start justify-between gap-6 flex-wrap">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--ds-color-ink-subtle)]">
                  Grant Writer · AI-assisted draft
                </p>
                <h1 className="mt-2 text-[20px] font-semibold md:text-[22px] tracking-tight text-[var(--ds-color-ink)]">
                  {proposal.title}
                </h1>
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  <span
                    data-testid="draft-status-pill"
                    className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em]"
                    style={{
                      background: meta.tintBg,
                      border: `1px solid ${meta.tintBorder}`,
                      color: meta.tint,
                    }}
                  >
                    {meta.label}
                  </span>
                  {proposal.version && (
                    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
                      Version {proposal.version}
                    </span>
                  )}
                </div>
              </div>
              <div
                data-testid="draft-budget-indicator"
                className="rounded-[var(--ds-radius-card)] bg-[var(--ds-color-surface-muted)]/60 border border-[var(--ds-color-border)] px-4 py-3 flex items-center gap-3"
              >
                {budgetLoading || budget == null ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-[var(--ds-color-ink-muted)]" />
                    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
                      Budget…
                    </span>
                  </>
                ) : (
                  <>
                    <Info className="w-3.5 h-3.5 text-[var(--ds-color-accent)]" />
                    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
                      <strong className="text-[var(--ds-color-ink)]">
                        {budget.remaining} of {budget.cap}
                      </strong>{" "}
                      section-generations remaining
                      {nextReset ? (
                        <>
                          {" "}
                          · Next reset:{" "}
                          <span className="text-[var(--ds-color-ink)]">{nextReset}</span>
                        </>
                      ) : null}
                    </span>
                  </>
                )}
              </div>
            </header>

            {/* Export dropdown (G4C CP2). Free-tier gets the inline
                upgrade CTA; feature-flag disabled state is handled
                inline by the dropdown component. Only shown when
                there is at least one section — otherwise the empty
                state directs the user back to step 9. */}
            {hasAnySection && (
              <div className="mt-6 flex justify-end" data-testid="draft-export-slot">
                <ExportDropdown
                  proposalId={proposal.id}
                  isFreeTier={isFreeTier}
                />
              </div>
            )}

            {!hasAnySection ? (
              <div
                data-testid="draft-empty-state"
                className="mt-10 rounded-[var(--ds-radius-card)] border border-dashed border-[var(--ds-color-border)] bg-[var(--ds-color-surface)]/40 p-10 flex flex-col items-center text-center gap-4"
              >
                <Target className="w-6 h-6 text-[var(--ds-color-accent)]" />
                <h3 className="text-[16px] font-semibold text-[var(--ds-color-ink)]">
                  No sections generated yet
                </h3>
                <p className="max-w-lg text-[13px] text-[var(--ds-color-ink-muted)]">
                  Return to the wizard, complete steps 1-8, then run
                  Generate AI Draft on step 9 to populate this view.
                </p>
                {proposal?.id && (
                  <Link
                    to={`/proposals/${proposal.id}/edit?step=9`}
                    data-testid="draft-empty-cta"
                    className="inline-flex items-center gap-2 rounded-[var(--ds-radius-md)] px-4 py-2 text-[13px] font-semibold text-[var(--ds-color-accent-ink)] bg-[var(--ds-color-accent)] hover:bg-[var(--ds-color-accent-strong)] transition-colors"
                  >
                    Return to wizard step 9
                  </Link>
                )}
              </div>
            ) : (
              <section className="mt-8 flex flex-col gap-4">
                {SECTION_KEYS.map((key) => {
                  const section = sections[key];
                  if (!section) return null;
                  return (
                    <SectionCard
                      key={key}
                      proposalId={proposal.id}
                      sectionKey={key}
                      section={section}
                      onUpdated={handleSectionUpdated}
                      onBudgetChanged={loadBudget}
                    />
                  );
                })}
                {/* G4D CP2 — AI Grant Coach panel on ProposalDraft.
                    Rendered ONLY for premium callers (free tier gets
                    the inline upgrade CTA above via FreeTierPreview
                    — Coach access on free tier is Wizard-only). The
                    outer conditional (proposal !== null → premium
                    path) has already guarded this branch. */}
                {!isFreeTier && (
                  <CoachPanel
                    proposalId={proposal.id}
                    proposal={proposal}
                    onProposalMutated={setProposal}
                    testIdPrefix="coach-panel-draft"
                  />
                )}
              </section>
            )}
          </>
        )}
      </div>

      {/* Persistent AI-assisted-draft footer strip (same shape as
          the wizard's — kept locally to avoid a shared abstraction
          this early). */}
      <footer
        data-testid="draft-disclosure-footer"
        className="fixed bottom-0 inset-x-0 z-20 border-t border-[var(--ds-color-border)] bg-[var(--ds-color-background)]/95 backdrop-blur px-4 md:px-8 py-3"
      >
        <div className="mx-auto max-w-6xl flex items-start gap-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
          <ShieldAlert className="w-4 h-4 flex-shrink-0 text-[var(--ds-color-accent)] mt-0.5" />
          <p className="leading-relaxed">
            <strong className="text-[var(--ds-color-ink)]">AI-assisted draft.</strong> Review
            carefully before submitting to any grant sponsor. Informed OS does
            not guarantee eligibility, awards, or funding decisions.
          </p>
        </div>
      </footer>
    </main>
  );
}
